import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Projects.css';
import { useLanguage } from '../../../../context/LanguageContext';
import translations from '../../../../translations';

// Import project images
import project1 from '../../../../assets/project1.webp';
import project2 from '../../../../assets/project2.webp';
import image28 from '../../../../assets/28.webp';
import project4Img from '../../../../assets/33.webp';
import Mishkat01 from '../../../../assets/Mishkat01.webp';
import Mosque01 from '../../../../assets/Mosque01.webp';
import image30 from '../../../../assets/30.webp';
import background5 from '../../../../assets/background5.webp';
import image130 from '../../../../assets/IMG-20250319-WA0130.webp';
import image39 from '../../../../assets/39.webp';
import image44 from '../../../../assets/44.webp';
import image49 from '../../../../assets/49 .webp';
import image55 from '../../../../assets/55.webp';
import image52 from '../../../../assets/52.webp';
import image35adli from '../../../../assets/35 ADLI.webp';
import imageAdmin2 from '../../../../assets/admin2.webp';
import image60 from '../../../../assets/60.webp';
import image47 from '../../../../assets/47.webp';

const useCountUp = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!shouldStart) {
      setCount(0);
      return;
    }
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(easeOutQuart(progress) * end));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    startTimeRef.current = null;
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [end, duration, shouldStart]);

  return count;
};

const Projects = () => {
  const { language, direction } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const isRTL = direction === 'rtl';

  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef(null);

  const handleIntersection = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) setIsVisible(true);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.3 });
    const currentRef = bannerRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [handleIntersection]);

  const areasCount = useCountUp(235, 2000, isVisible);
  const projectsCount = useCountUp(170, 2000, isVisible);
  const valueCount = useCountUp(247, 2000, isVisible);

  // Featured project (Residential Compound - largest project)
  const featuredProject = useMemo(() => ({
    id: 2,
    slug: 'private-villa-project',
    title: t.project2Title,
    description: t.project2Description,
    details: language === 'en'
      ? 'One of the biggest residential compounds in Riyadh'
      : 'أحد أكبر المجمعات السكنية في الرياض',
    cost: language === 'en' ? '90 Million' : '90 مليون',
    image: project2,
    width: 1490,
    height: 1876
  }), [language, t.project2Title, t.project2Description]);

  // Other projects for the grid
  const otherProjects = useMemo(() => [
    {
      id: 1,
      slug: 'al-nada-sharafat',
      title: t.project1Title,
      description: t.project1Details || t.project1Description,
      image: project1,
      width: 2682,
      height: 1892
    },
    {
      id: 3,
      slug: 'starta-villa-project',
      title: t.project3Title,
      description: t.project3Description,
      image: image28,
      width: 1414,
      height: 1876
    },
    {
      id: 4,
      slug: 'al-ahsa-construction-station',
      title: t.project4Title,
      description: t.project4Description,
      image: project4Img,
      width: 1284,
      height: 1279
    },
    {
      id: 5,
      slug: 'mishkah-association-project',
      title: t.project5Title,
      description: t.project5Description,
      image: Mishkat01,
      width: 1600,
      height: 1108
    },
    {
      id: 6,
      slug: 'al-waqf-mosque-project',
      title: t.project6Title,
      description: t.project6Description,
      image: Mosque01,
      width: 1600,
      height: 1108
    },
    {
      id: 7,
      slug: 'riwaq-al-madinah',
      title: t.project7Title,
      description: t.project7Description,
      image: image30,
      width: 1414,
      height: 1876
    },
    {
      id: 8,
      slug: 'sports-club-project',
      title: t.project8Title,
      description: t.project8Description,
      image: image130,
      width: 1600,
      height: 1108
    },
    {
      id: 9,
      slug: 'sports-track-project',
      title: t.project9Title,
      description: t.project9Description,
      image: image39,
      width: 1600,
      height: 1108
    },
    {
      id: 10,
      slug: 'al-sharqi-avenue-project',
      title: t.project10Title,
      description: t.project10Description,
      image: image44,
      width: 1600,
      height: 1108
    },
    {
      id: 11,
      slug: 'al-awali-makkah-project',
      title: t.project11Title,
      description: t.project11Description,
      image: image49,
      width: 1600,
      height: 1108
    },
    {
      id: 12,
      slug: 'specialized-medical-clinics',
      title: t.project12Title,
      description: t.project12Description,
      image: image55,
      width: 1600,
      height: 1108
    },
    {
      id: 13,
      slug: 'sharafat-park-al-khobar',
      title: t.project13Title,
      description: t.project13Description,
      image: image52,
      width: 1600,
      height: 1108
    },
    {
      id: 14,
      slug: 'justice-training-center',
      title: t.project14Title,
      description: t.project14Description,
      image: image35adli,
      width: 1600,
      height: 1108
    },
    {
      id: 15,
      slug: 'office-projects',
      title: t.project15Title,
      description: t.project15Description,
      image: imageAdmin2,
      width: 1600,
      height: 1108
    },
    {
      id: 16,
      slug: 'princess-abeer-complex',
      title: t.project16Title,
      description: t.project16Description,
      image: image60,
      width: 1600,
      height: 1108
    },
    {
      id: 17,
      slug: 'prophets-mosque-housing-renovation',
      title: t.project17Title,
      description: t.project17Description,
      image: image47,
      width: 1600,
      height: 1108
    },
  ], [t.project1Title, t.project1Description, t.project1Details, t.project3Title, t.project3Description, t.project3Details, t.project4Title, t.project4Description, t.project5Title, t.project5Description, t.project6Title, t.project6Description, t.project7Title, t.project7Description, t.project8Title, t.project8Description, t.project9Title, t.project9Description, t.project10Title, t.project10Description, t.project11Title, t.project11Description, t.project12Title, t.project12Description, t.project13Title, t.project13Description, t.project14Title, t.project14Description, t.project15Title, t.project15Description, t.project16Title, t.project16Description, t.project17Title, t.project17Description]);

  const [currentPage, setCurrentPage] = useState(0);
  const projectsPerPage = 4;
  const totalPages = Math.ceil(otherProjects.length / projectsPerPage);

  const currentProjects = otherProjects.slice(
    currentPage * projectsPerPage,
    (currentPage + 1) * projectsPerPage
  );

  return (
    <section className={`projects-insights-section ${isRTL ? 'rtl' : ''}`} id="projects">
      <div className="projects-insights-container">
        <div className="projects-insights-header">
          <h2 className="projects-insights-title">{t.projectsTitle}</h2>
          <div className="projects-insights-divider"></div>
        </div>
      </div>

      {/* Stats Banner */}
      <div className={`project-stats-banner ${isRTL ? 'rtl' : ''}`} ref={bannerRef}>
        <div className="stats-banner-bg">
          <img
            src={background5}
            alt=""
            aria-hidden="true"
            width="1702"
            height="566"
            loading="lazy"
            decoding="async"
          />
          <div className="stats-banner-overlay"></div>
        </div>
        <div className="stats-banner-content">
          {/* Main Stats Row */}
          <div className="stats-banner-items">
            <div className="stats-banner-item">
              <span className="stats-banner-label">{t.statsExecutedAreas}</span>
              <span className="stats-banner-number">{areasCount}+</span>
              <span className="stats-banner-unit">{t.statsThousandSqm}</span>
            </div>
            <div className="stats-banner-divider"></div>
            <div className="stats-banner-item">
              <span className="stats-banner-label">{t.statsNumberOfProjects}</span>
              <span className="stats-banner-number">{projectsCount}+</span>
              <span className="stats-banner-sub">{t.statsProject}</span>
              <span className="stats-banner-unit stats-years">{t.statsProjectsYears}</span>
            </div>
            <div className="stats-banner-divider"></div>
            <div className="stats-banner-item">
              <span className="stats-banner-label">{t.statsValueOfProjects}</span>
              <div className="stats-banner-number-row">
                <span className="stats-banner-number">{valueCount}+</span>
                <svg className="stats-banner-sar-icon" width="36" height="36" viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M133.663 56.3338C140.414 48.7553 144.563 45.3529 152.713 41.0513V167.247L133.663 171.184V56.3338Z" fill="white" />
                  <path d="M194.529 119.779C198.478 111.443 198.943 107.738 200 99.187L53.9804 130.894C50.5177 138.607 49.4031 142.918 48.8695 150.576L194.529 119.779Z" fill="white" />
                  <path d="M194.529 158.433C198.478 150.097 198.943 146.392 200 137.84L134.36 151.733C133.895 159.375 134.429 163.295 133.895 170.952L194.529 158.433Z" fill="white" />
                  <path d="M194.529 197.08C198.478 188.744 198.943 185.04 200 176.488L140.168 189.477C137.148 193.645 135.289 200.591 133.895 209.6L194.529 197.08Z" fill="white" />
                  <path d="M99.5134 183.919C105.321 176.741 111.361 167.711 115.543 160.301L45.1108 175.567C41.6481 183.28 40.5335 187.592 39.9999 195.249L99.5134 183.919Z" fill="white" />
                  <path d="M96.4934 45.6824C103.245 38.1039 107.394 34.7015 115.543 30.3999V160.764L96.4934 164.7V45.6824Z" fill="white" />
                </svg>
              </div>
              <span className="stats-banner-sub">{t.statsMillionSAR}</span>
              <span className="stats-banner-unit stats-years">{t.statsValueYears}</span>
            </div>
          </div>

          {/* Category Grid */}
          <div className="stats-category-grid">
            <div className="stats-category-row">
              <div className="stats-cat-card edge">
                <span className="stats-cat-number">10+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Commercial Complexes' : 'مجمعات تجارية'}</span>
              </div>
              <div className="stats-cat-card middle">
                <span className="stats-cat-number">40+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Residential Buildings' : 'مباني سكنية'}</span>
              </div>
              <div className="stats-cat-card edge">
                <span className="stats-cat-number">5+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Office Spaces' : 'مساحات مكتبية'}</span>
              </div>
            </div>
            <div className="stats-category-row">
              <div className="stats-cat-card edge">
                <span className="stats-cat-number">5+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Educational Buildings' : 'مباني تعليمية'}</span>
              </div>
              <div className="stats-cat-card middle">
                <span className="stats-cat-number">1+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Medical Clinics' : 'عيادات طبية'}</span>
              </div>
              <div className="stats-cat-card edge">
                <span className="stats-cat-number">105+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Residential Units' : 'وحدات سكنية'}</span>
              </div>
            </div>
            <div className="stats-category-row">
              <div className="stats-cat-card edge">
                <span className="stats-cat-number">10+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Residential Complexes' : 'مجمعات سكنية'}</span>
              </div>
              <div className="stats-cat-card middle">
                <span className="stats-cat-number">2+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Sports Clubs' : 'أندية رياضية'}</span>
              </div>
              <div className="stats-cat-card middle">
                <span className="stats-cat-number">2+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Infrastructure Projects' : 'مشاريع بنية تحتية'}</span>
              </div>
              <div className="stats-cat-card middle">
                <span className="stats-cat-number">10+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Maintenance & Operation Projects' : 'مشاريع صيانة وتشغيل'}</span>
              </div>
              <div className="stats-cat-card edge">
                <span className="stats-cat-number">10+</span>
                <span className="stats-cat-label">{language === 'en' ? 'Government & Semi-Government Buildings' : 'مباني حكومية وشبه حكومية'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Cards */}
      <div className="projects-insights-container">
        <div className="projects-insights-grid">
          {/* Featured Project - Large Card on Left */}
          <div className="featured-project-card" onClick={() => navigate(`/${language}/projects/${featuredProject.slug}`)}>
            <div className="featured-project-image">
              <img
                src={featuredProject.image}
                alt={featuredProject.title}
                width={featuredProject.width}
                height={featuredProject.height}
                decoding="async"
              />
            </div>
            <div className="featured-project-content">
              <h3 className="featured-project-title">{featuredProject.title}</h3>
              <p className="featured-project-details">{featuredProject.details}</p>
              <div className="read-more-link">
                <span>{t.projectReadMore}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>

          {/* Right Side Grid - Smaller Cards */}
          <div className="projects-grid-right-container">
            <div className="projects-grid-right">
              {currentProjects.map((project, index) => (
                <div
                  className="project-grid-card"
                  key={`${project.id}-${index}`}
                  onClick={() => navigate(`/${language}/projects/${project.slug}`)}
                >
                  <div className="project-grid-image">
                    <img
                      src={project.image}
                      alt={project.title}
                      width={project.width}
                      height={project.height}
                      decoding="async"
                    />
                  </div>
                  <div className="project-grid-content">
                    <h4 className="project-grid-title">{project.title}</h4>
                    <p className="project-grid-description">{project.description}</p>
                    <div className="read-more-link small">
                      <span>{t.projectReadMore}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="projects-pagination">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <span
                    key={index}
                    className={`pagination-dot ${index === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;